/**
 * Feedback API Service
 * Handles user feedback submission
 * Uses VITE_API_URL (backend.duneworks.ai)
 */

import apiClient from "./client";

export interface FeedbackData {
  overall: number; // 1-10 (emoji 1-5 is converted to 2,4,6,8,10 or 1-5)
  design: number; // 1-10 (stars 1-5 converted to 2,4,6,8,10)
  intuitive: number; // 1-10
  rca: number; // 1-10
  suggestions?: string | null;
  runbook?: string | null; // base64 encoded PDF
}

export interface FeedbackResponse {
  id: string;
  userId: string;
  overall: number;
  design: number;
  intuitive: number;
  rca: number;
  suggestions: string | null;
  hasRunbook: boolean;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export class FeedbackService {
  /**
   * Submit feedback
   * Endpoint: POST /api/v1/feedback
   * Extension format: JSON body with ratings 1-10
   */
  static async submitFeedback(
    feedbackData: FeedbackData,
  ): Promise<FeedbackResponse> {
    const payload = {
      overall: feedbackData.overall,
      design: feedbackData.design,
      intuitive: feedbackData.intuitive,
      rca: feedbackData.rca,
      suggestions: feedbackData.suggestions || null,
      runbook: feedbackData.runbook || null,
    };

    console.log("Submitting feedback:", {
      overall: payload.overall,
      design: payload.design,
      intuitive: payload.intuitive,
      rca: payload.rca,
      hasSuggestions: !!payload.suggestions,
      hasRunbook: !!payload.runbook,
    });

    return apiClient.post<FeedbackResponse>("/api/v1/feedback", payload);
  }

  /**
   * Convert file to base64 for upload
   */
  static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          // Remove data:application/pdf;base64, prefix
          const base64 = reader.result.split(",")[1];
          resolve(base64);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = () => reject(new Error("Error reading file"));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Validate feedback data before submission
   */
  static validateFeedback(data: Partial<FeedbackData>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check required fields (1-10 range for backend)
    if (
      !data.overall ||
      !Number.isInteger(data.overall) ||
      data.overall < 1 ||
      data.overall > 10
    ) {
      errors.push("Overall rating must be between 1 and 10");
    }

    if (
      !data.design ||
      !Number.isInteger(data.design) ||
      data.design < 1 ||
      data.design > 10
    ) {
      errors.push("Design rating must be between 1 and 10");
    }

    if (
      !data.intuitive ||
      !Number.isInteger(data.intuitive) ||
      data.intuitive < 1 ||
      data.intuitive > 10
    ) {
      errors.push("Intuitive rating must be between 1 and 10");
    }

    if (
      !data.rca ||
      !Number.isInteger(data.rca) ||
      data.rca < 1 ||
      data.rca > 10
    ) {
      errors.push("RCA rating must be between 1 and 10");
    }

    // Validate suggestions length if provided
    if (data.suggestions && data.suggestions.length > 5000) {
      errors.push("Suggestions must be less than 5000 characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
